import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_NAME, canonicalUrl, findPublicRoute } from "./publicRoutes";

const setMeta = (selector: string, attr: "name" | "property", key: string, content: string | null) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (content === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href: string | null) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
};

/**
 * Keeps the document head in step with the current route while browsing.
 * Prerendered pages ship the same values in their HTML (scripts/prerender.mjs);
 * app pages get a plain title and no canonical.
 */
export function RouteMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const route = findPublicRoute(pathname);
    if (!route) {
      document.title = SITE_NAME;
      setCanonical(null);
      setMeta('meta[name="robots"]', "name", "robots", "noindex");
      return;
    }
    const url = canonicalUrl(route);
    document.title = route.title;
    setCanonical(url);
    setMeta('meta[name="description"]', "name", "description", route.description);
    setMeta('meta[name="robots"]', "name", "robots", route.index ? "index, follow, max-image-preview:large, max-snippet:-1" : "noindex, follow");
    setMeta('meta[property="og:title"]', "property", "og:title", route.title);
    setMeta('meta[property="og:description"]', "property", "og:description", route.description);
    setMeta('meta[property="og:url"]', "property", "og:url", url);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", route.title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", route.description);
  }, [pathname]);

  return null;
}
